import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Search, FileText, Shield, FileCheck, Users, BarChart, ChevronRight, Zap, Clock, Database, LineChart } from 'lucide-react';
import '../styles/globals.css';
import './PricingPage.css';

const PricingPage = () => {
  const corePlatform = [
    {
      title: "Assistant",
      icon: MessageSquare,
      features: [
        "AI-Powered Document Interaction",
        "Interact with the organizational knowledge base through conversation",
        "Query document content using an intuitive chat interface",
        "General question-answering capabilities",
        "Web search functionality integrated",
        "Persistent chat history for ongoing conversations",
        "Advanced filtering options (by files, tags, projects)",
        "Customizable instructions for tailored interactions"
      ]
    },
    {
      title: "Search",
      icon: Search,
      features: [
        "Semantic Search Engine",
        "Context-aware document retrieval beyond keyword matching",
        "Returns both complete documents and relevant text chunks",
        "Understands the meaning behind search queries",
        "Comprehensive filtering system",
        "Sort and prioritize results based on relevance"
      ]
    },
    {
      title: "Files",
      icon: FileText,
      features: [
        "Document Management System",
        "Support for multiple file formats",
        "Secure document storage and organization",
        "Permission-based sharing capabilities",
        "Quick Notes Feature",
        "Instantly capture and index text snippets",
        "Seamless integration of external content",
        "No file creation necessary for small pieces of information"
      ]
    },
    {
      title: "Admin",
      icon: Shield,
      features: [
        "Comprehensive Administration",
        "User account management and provisioning",
        "Group-based access control",
        "System usage analytics and reporting",
        "Storage utilization monitoring",
        "Security settings and compliance controls"
      ]
    }
  ];

  const expansionModules = [
    {
      title: "BidAI",
      icon: FileCheck,
      features: [
        "Automated RFP Response",
        "Smart RFP template extraction",
        "AI-powered response generation",
        "Uses organizational documentation for accurate proposals",
        "Streamlines the bid response process"
      ]
    },
    {
      title: "RecruitAI",
      icon: Users,
      features: [
        "Dual-Purpose Talent Matching",
        "Talent Search: Match job descriptions to candidate resumes",
        "Job Search: Match candidate profiles to available positions",
        "Resume and job description parsing",
        "AI-powered matching algorithms",
        "Reduces recruitment cycle time",
        "Data exchange with RecruitCRM",
        "Synchronize candidate information",
        "Streamline recruitment workflow between systems"
      ]
    },
    {
      title: "Report Builder (Beta)",
      icon: BarChart,
      features: [
        "Intelligent Data Analysis",
        "CSV data import and processing",
        "Dynamic grouping and aggregation",
        "Natural language report configuration",
        "Automated report generation based on instructions",
        "Multi-dimensional analysis with dual grouping capability"
      ]
    }
  ];

  const platformBenefits = [
    {
      title: "Knowledge Democratization",
      description: "Provides organizational knowledge to all employees",
      icon: Database
    },
    {
      title: "Efficiency Enhancement",
      description: "Reduces time spent searching for information",
      icon: Clock
    },
    {
      title: "Decision Support",
      description: "Offers data-driven insights for better business decisions",
      icon: LineChart
    },
    {
      title: "Process Automation",
      description: "Streamlines document-heavy workflows",
      icon: Zap
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 50
      }
    }
  };

  return (
    <div className="pricing-page">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="container">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Enterprise <span className="gradient-text">AI Platform</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="hero-subtitle"
          >
            Ufficient Enterprise AI Version 1.2.1 - Transforming how organizations manage and leverage knowledge
          </motion.p>
          <motion.div 
            className="hero-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            <a href="/contact" className="contact-sales-button">
              Contact Sales
              <ChevronRight size={18} />
            </a>
            <p className="pricing-note">Custom pricing based on your organization's needs</p>
          </motion.div>
        </div>
      </div>

      {/* Core Platform */}
      <div className="features-section">
        <div className="container">
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            Core Platform
          </motion.h2>
          <motion.div 
            className="features-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {corePlatform.map((feature, index) => (
              <motion.div 
                key={index} 
                className="feature-card"
                variants={itemVariants}
              >
                <div className="feature-header">
                  <div className="feature-icon-container">
                    <feature.icon className="feature-icon" />
                  </div>
                  <h3>{feature.title}</h3>
                </div>
                <ul className="feature-list">
                  {feature.features.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Expansion Modules */}
      <div className="features-section expansion-section">
        <div className="container">
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Expansion Modules
          </motion.h2>
          <motion.div 
            className="features-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {expansionModules.map((feature, index) => (
              <motion.div 
                key={index} 
                className="feature-card expansion-card"
                variants={itemVariants}
              >
                <div className="feature-header">
                  <div className="feature-icon-container">
                    <feature.icon className="feature-icon" />
                  </div>
                  <h3>{feature.title}</h3>
                </div>
                <ul className="feature-list">
                  {feature.features.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Platform Benefits */}
      <div className="benefits-section">
        <div className="container">
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Platform Benefits
          </motion.h2>
          <motion.div 
            className="benefits-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {platformBenefits.map((benefit, index) => (
              <motion.div 
                key={index} 
                className="benefit-card"
                variants={itemVariants}
              >
                <div className="benefit-icon-container">
                  <benefit.icon className="benefit-icon" />
                </div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Contact CTA */}
      <div className="cta-section">
        <div className="container">
          <motion.div 
            className="cta-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Ready to transform your organization?</h2>
            <p>Our team will work with you to customize an enterprise solution that meets your specific needs and challenges.</p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;